import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Alert, TextField, Button, Typography, CircularProgress, Divider } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Datagrid from "../Datagrid/Datagrid";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { getPriceMarkups, updatePriceMarkup } from "../../services/priceMarkupService";

const PriceMarkup = () => {
	const { t } = useTranslation();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [editRow, setEditRow] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [markupValue, setMarkupValue] = useState("");
	const [saving, setSaving] = useState(false);

	const fetchRows = async () => {
		setLoading(true);
		setError("");
		try {
			const response = await getPriceMarkups();
			const data = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
			setRows(data);
		} catch (e) {
			console.error("Failed to load price markups", e);
			setError(e?.message || t('ALERT_MESSAGES.ERROR'));
			setRows([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchRows();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleEdit = (row) => {
		setEditRow(row);
		setMarkupValue(String(row?.markup ?? 0));
		setModalOpen(true);
	};

	const handleMarkupChange = (event) => {
		const { value } = event.target;
		if (value === "" || value === "-" || value === "+" || value === "." || value === "-." || value === "+.") {
			setMarkupValue(value);
			return;
		}
		const numericValue = Number(value);
		if (Number.isNaN(numericValue)) return;
		if (numericValue > 100) {
			setMarkupValue("100");
			return;
		}
		if (numericValue < -100) {
			setMarkupValue("-100");
			return;
		}
		setMarkupValue(value);
	};

	const handleModalClose = () => {
		setModalOpen(false);
		setEditRow(null);
		setSaving(false);
	};

	const handleSave = async () => {
		if (!editRow) return;
		setSaving(true);
		try {
			await updatePriceMarkup(editRow.id, { markup: Number(markupValue) });
			handleModalClose();
			await fetchRows();
			Swal.fire({ icon: "success", title: t('ALERT_MESSAGES.SUCCESS'), text: t('ALERT_MESSAGES.UPDATED_SUCCESSFULLY') || "Updated successfully" });
		} catch (e) {
			console.error("Failed to update price markup", e);
			Swal.fire({ icon: "error", title: t('ALERT_MESSAGES.ERROR'), text: e?.response?.data?.message || e?.message || "Failed to update price markup" });
		} finally {
			setSaving(false);
		}
	};

	const formatMarkup = (value) => {
		const numeric = Number(value || 0);
		const descriptor = numeric >= 0 ? t('LABELS.MARKUP') || "Markup" : t('LABELS.DISCOUNT') || "Discount";
		return `${numeric > 0 ? "+" : ""}${numeric}% ${descriptor}`;
	};

	const columns = useMemo(() => [
		{ field: "from", headerName: t('TABLE_HEADERS.FROM'), flex: 1 },
		{ field: "to", headerName: t('TABLE_HEADERS.TO'), flex: 1 },
		{
			field: "markup",
			headerName: t('TABLE_HEADERS.MARKUP_PERCENT'),
			flex: 1,
			renderCell: (params) => (
				<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>

					<Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center' }}>

						<Typography
							variant="body2"
							sx={{
								fontWeight: 600,
								color: Number(params.row?.markup || 0) >= 0 ? "success.main" : "error.main",
							}}
						>
							{formatMarkup(params.row?.markup)}
						</Typography>
					</Box>
				</Box>
			),
		},
		{
			field: "actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 120,
			sortable: false,
			filterable: false,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Tooltip title={t('BUTTON.EDIT') || "Edit"}>
					<IconButton onClick={() => handleEdit(params.row)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
			),
		},
	], [t]);

	const renderTable = () => {
		if (loading) return <DatagridLoading columns={4} rows={5} />;
		if (error) return <Alert severity="error">{error}</Alert>;
		if (!rows.length) {
			return (
				<Alert severity="info">
					{t('UI_TEXT.NO_PRICE_MARKUP_DATA') || "No price markup ranges found in the system."}
				</Alert>
			);
		}
		const sortedRows = [...rows].sort((a, b) => Number(b?.markup || 0) - Number(a?.markup || 0));
		return (
			<Datagrid
				data={sortedRows}
				columns={columns}
				rowIdField="id"
				showSearchBar
				checkboxSelection={false}
				autoHeight
				rowClick
				onRowClick={(params) => handleEdit(params.row)}
			/>
		);
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			{renderTable()}
			<ModalWrapper
				open={modalOpen}
				setOpen={(open) => !open ? handleModalClose() : setModalOpen(true)}
				onClose={handleModalClose}
				title={t('LABELS.UPDATE_PRICE_MARKUP') || "Update Price Markup"}
				subtitle=""
				width="420px"
				content={
					editRow ? (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								flexWrap: "wrap",
								gap: 2,
								alignItems: "stretch",
								justifyContent: "space-between",
							borderRadius: 2,
							px: 3,
							py: 2,
							border: (theme) => `1px solid ${theme.palette.divider}`,
							}}
						>
							<Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
								<Typography variant="body1" color="text.secondary">
									{t('TABLE_HEADERS.FROM')}
								</Typography>
								<Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
									{editRow.from}
								</Typography>
							</Box>
							<Divider orientation="vertical" flexItem sx={{ borderColor: (theme) => theme.palette.divider, opacity: 1 }} />
							<Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 0.5 }}>
								<Typography variant="body1" color="text.secondary">
									{t('TABLE_HEADERS.TO')}
								</Typography>
								<Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
									{editRow.to}
								</Typography>
							</Box>
						</Box>
						<TextField
							label={t('LABELS.MARKUP_PERCENT')}
							type="number"
							value={markupValue}
							onChange={handleMarkupChange}
							inputProps={{ min: -100, max: 100 }}
							fullWidth
						/>
							<Alert severity={Number(markupValue || 0) >= 0 ? "success" : "warning"}>
								{Number(markupValue || 0) >= 0 ? t('LABELS.MARKUP') : t('LABELS.DISCOUNT')} • {formatMarkup(markupValue)}
							</Alert>
						</Box>
					) : (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4, px: 1 }}>
							<CircularProgress />
						</Box>
					)
				}
				actions={
					<>
						<Button onClick={handleModalClose}>{t('BUTTON.CANCEL')}</Button>
						<Button variant="contained" onClick={handleSave} disabled={saving}>
							{saving ? `${t('BUTTON.SAVING')}` : t('BUTTON.SAVE')}
						</Button>
					</>
				}
			/>
		</Box>
	);
};

export default PriceMarkup;
